import { CircularProgress, Alert } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SmallForm from './small-form';

import Arranger from '~c/components/base/arranger';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import { nodeAuthApi } from '~c/services';

const MagicLogin: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [magicLogin, { isLoading }] = nodeAuthApi.useMagicLoginMutation();
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const performMagicLogin = async () => {
      if (!token) {
        setError('Invalid magic link. Missing token.');
        return;
      }

      try {
        const result = await magicLogin({ token });

        if ('data' in result) {
          const { data } = result;

          if (!data.email_verified) {
            setError('User email not verified');
            setTimeout(() => navigate('/sign-in'), 3000);
            return;
          }

          if (!data.is_active) {
            setError('Your account is not activated yet by the admin.');
            setTimeout(() => navigate('/sign-in'), 3000);
            return;
          }

          // Success - redirect to dashboard
          navigate('/dashboard/compliance'); // route guard redirects MEMBER/AUDITOR to /dashboard/tasks
        } else if ('error' in result) {
          const errorResponse = result.error as {
            data?: {
              error?: string;
              message?: string;
              payload?: { message?: string };
            };
            status?: number;
            message?: string;
          };

          let errorMessage =
            'Magic link authentication failed. Please try signing in normally.';

          if (errorResponse.data?.message) {
            errorMessage = errorResponse.data.message;
          } else if (errorResponse.data?.payload?.message) {
            errorMessage = errorResponse.data.payload.message;
          } else if (errorResponse.data?.error) {
            errorMessage = errorResponse.data.error;
          } else if (errorResponse.message) {
            errorMessage = errorResponse.message;
          }

          setError(errorMessage);
          setTimeout(() => navigate('/sign-in'), 3000);
        }
      } catch (err) {
        console.error('Magic login error:', err);
        setError(
          'An unexpected error occurred. Please try signing in normally.'
        );
        setTimeout(() => navigate('/sign-in'), 3000);
      }
    };

    performMagicLogin();
  }, [token, magicLogin, navigate]);

  if (isLoading) {
    return (
      <SmallForm
        title={
          <Arranger direction="column" alignItems="center">
            <Spacer mb={spc.xs} />
            <Text sz="body16" sx={{ pt: spc.xxxs }}>
              Authenticating...
            </Text>
          </Arranger>
        }
      >
        <Arranger direction="column" alignItems="center" sx={{ py: spc.xl }}>
          <CircularProgress size={48} />
          <Spacer mb={spc.m} />
          <Text sz="body14" color="textSecondary">
            Please wait while we log you in
          </Text>
        </Arranger>
      </SmallForm>
    );
  }

  if (error) {
    return (
      <SmallForm
        title={
          <Arranger direction="column" alignItems="center">
            <Spacer mb={spc.xs} />
            <Text sz="body16" sx={{ pt: spc.xxxs }}>
              {error}
            </Text>
          </Arranger>
        }
      >
        <Text sz="body14" color="textSecondary" align="center">
          Redirecting to sign-in page in a few seconds...
        </Text>
      </SmallForm>
    );
  }

  return null;
};

export default MagicLogin;
